/**
 * galaxy-lrsa-rings.ts — MFix31.1 galaxy-view LRSA detection-radius
 * visualisation per playtest feedback on the v4M10.6 cross-system
 * reveal.
 *
 * After v4M10.6, building an LRSA reveals nearby systems to the Fog
 * of War layer, but the player has no in-game cue for *how far*. This
 * module renders an empire-tinted ring centred on each LRSA's parent
 * system at galaxy-frame radius `rangeByLevel[level - 1]`, drawn in
 * galaxy view only.
 *
 * Per-empire scope: the viewer + every teammate per `docs/TEAMS.md §3`
 * vision union — teammates' LRSAs contribute to the viewer's FoW
 * via the resolver, so the player should see those rings too. Each
 * ring is tinted by its OWNING empire's colour so the player can
 * tell which empire's LRSA is generating which ring.
 *
 * Enemy LRSAs never render — same intel-asymmetry as everywhere else
 * in the renderer.
 *
 * Drawn after `drawGalaxyAdjacency` and before the Warp Gate pass so
 * the ring sits underneath all transient cues (jump-target picker,
 * paths, ship dots) but above the bare star field.
 */

import { worldToScreen, type Viewport } from './camera';
import type { Camera } from '../ui/state';
import type { UiState } from '../ui/state';
import { viewerEmpireId } from '../ui/state';
import { areTeammates } from '../game/teams';
import { isSensorRangeGalaxyEffect } from '../game/balance-structure-effects';
import type { EmpireId, GameState, SystemId } from '../state/types/index';

export interface LrsaRing {
  /** System whose centre the ring is drawn around. */
  systemId: SystemId;
  /** Galaxy-frame reveal radius (0..1 coords). */
  radius: number;
  /** LRSA level (1..6) — surfaced for future label / tooltip use. */
  level: number;
  /** Empire id owning the LRSA — used to pick the ring colour. */
  ownerEmpireId: EmpireId;
}

/**
 * Collect every LRSA-emitted ring that should render for the viewer:
 * viewer's own LRSAs + every teammate's LRSAs (`SENSORS.md §2.6`
 * team-vision union). Enemy LRSAs never enter the result.
 *
 * Exported separately from the draw helper so tests can assert the
 * collected data without faking a canvas context.
 */
export function collectLrsaRingsForViewer(
  state: GameState,
  viewerId: EmpireId,
): LrsaRing[] {
  const rings: LrsaRing[] = [];
  const viewer = state.empires.get(viewerId);
  if (!viewer) return rings;
  for (const empire of state.empires.values()) {
    const isSelf = empire.id === viewerId;
    if (!isSelf && !areTeammates(state, viewerId, empire.id)) continue;
    for (const pid of empire.planetIds) {
      const planet = state.planets.get(pid);
      if (!planet) continue;
      // Defensive: a planet that has rolled over to another empire mid-
      // tick (post Annex / bombardment-flip) might still appear in the
      // old owner's planetIds set briefly. Cross-check ownership before
      // attributing an LRSA ring to the empire.
      if (planet.ownerEmpireId !== empire.id) continue;
      for (const sid of planet.structureIds) {
        const structure = state.structures.get(sid);
        if (!structure) continue;
        if (structure.kind !== 'long-range-sensor-array') continue;
        if (structure.level < 1) continue;
        if (structure.currentHull <= 0) continue;
        const row = state.structuresData.byKind[structure.kind];
        if (!row) continue;
        for (const effect of row.effects) {
          if (!isSensorRangeGalaxyEffect(effect)) continue;
          const radius = effect.rangeByLevel[structure.level - 1] ?? 0;
          if (radius <= 0) continue;
          rings.push({
            systemId: planet.systemId,
            radius,
            level: structure.level,
            ownerEmpireId: empire.id,
          });
        }
      }
    }
  }
  return rings;
}

/**
 * Project a galaxy-frame circular radius `r` into the elliptical
 * screen-space projection produced by `worldToScreen`, which scales
 * x by `viewW` and y by `viewH` separately (per `camera.ts`). On a
 * non-square viewport, a galaxy-frame disk of radius `r` projects to
 * an ellipse with semi-axes `(r × zoom × viewW, r × zoom × viewH)`.
 *
 * MFix37 (2026-05-30) — the previous `projectGalaxyRadius` collapsed
 * this to a single circle using `min(viewW, viewH)`, which drew a
 * ring SMALLER than the actual reach on the longer viewport axis.
 * Systems inside the disk on the longer axis appeared outside the
 * ring while drilling in still showed planets+terrain (the sim
 * correctly said they were revealed). Returning two semi-axes lets
 * the ring renderer draw an ellipse that matches the actual reach.
 *
 * Centralised here so the test can verify the scaling without
 * spinning up a canvas.
 */
export function projectGalaxyRadii(
  camera: Camera,
  vp: Viewport,
  galaxyRadius: number,
): { rx: number; ry: number } {
  return {
    rx: galaxyRadius * camera.zoom * vp.viewW,
    ry: galaxyRadius * camera.zoom * vp.viewH,
  };
}

/**
 * Render every viewer-eligible LRSA ring on `ctx`. No-op in non-galaxy
 * view modes. Rings draw as a soft empire-tinted stroke at ~1.5 px
 * lineWidth + alpha 0.35 so the underlying star + per-system halo +
 * ship dots stay readable when zoomed in.
 */
export function drawGalaxyLrsaRings(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
): void {
  if (ui.viewMode !== 'galaxy') return;
  const viewerId = viewerEmpireId(state, ui);
  const rings = collectLrsaRingsForViewer(state, viewerId);
  if (rings.length === 0) return;

  ctx.save();
  for (const ring of rings) {
    const sys = state.systems.get(ring.systemId);
    if (!sys) continue;
    const center = worldToScreen(ui.galaxyCamera, vp, { x: sys.x, y: sys.y });
    // MFix37 — ellipse semi-axes match the actual reach disk projected
    // through `worldToScreen` (x → viewW, y → viewH separately). Reads
    // as an oval on non-square viewports; that's correct — the sim's
    // circular reach in galaxy-frame coords IS an ellipse in screen
    // coords because the renderer projects x/y non-uniformly.
    const { rx, ry } = projectGalaxyRadii(ui.galaxyCamera, vp, ring.radius);
    // Skip rings that have collapsed to sub-pixel size on either axis
    // at the current zoom — drawing them just adds aliasing without
    // communicating anything.
    if (Math.max(rx, ry) < 2) continue;
    const owner = state.empires.get(ring.ownerEmpireId);
    const color = owner?.color ?? 'rgba(150, 165, 195, 0.65)';
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.35;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.ellipse(center.x, center.y, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();

    // MFix35 (2026-05-30) — discoverability label. Playtest revealed
    // the ring's meaning wasn't obvious: players couldn't tell whether
    // it represented scan range, detection range, or sensor reveal.
    // Anchor the label at the ring's bottom edge so it doesn't overlap
    // the per-system indicator stack which lives above the star.
    // Suppress at small ring sizes so it doesn't clutter the galaxy
    // view when zoomed out.
    if (ry >= 32) {
      ctx.setLineDash([]);
      ctx.globalAlpha = 0.6;
      ctx.fillStyle = color;
      ctx.font = '10px inherit';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(`Sensor reveal · L${ring.level}`, center.x, center.y + ry + 4);
      ctx.setLineDash([6, 4]);
    }
  }
  ctx.setLineDash([]);
  ctx.restore();
}
